import React, { useEffect, useState } from 'react';
import { Modal, Stack, MultiSelect, Group, Button, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { ReviewRequest, User } from '../../types';
import { reviewerId } from '../../utils/reviewProgress';

interface AssignReviewersModalProps {
  opened: boolean;
  request: ReviewRequest | null;
  onClose: () => void;
  onSave: (reviewerIds: string[]) => Promise<void> | void;
  isSaving?: boolean;
}

/** Admin modal to manually set/replace the required reviewers of a request. */
export const AssignReviewersModal: React.FC<AssignReviewersModalProps> = ({
  opened,
  request,
  onClose,
  onSave,
  isSaving = false,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>([]);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => api('/users'),
    staleTime: 5 * 60 * 1000,
    enabled: opened,
  });

  useEffect(() => {
    if (opened && request) {
      setSelected((request.requiredReviewers || []).map(reviewerId));
    }
  }, [opened, request]);

  const applicantId = request?.applicant?._id;

  return (
    <Modal opened={opened} onClose={onClose} title={t('reviewRequests.assignReviewers')} size="md">
      <Stack gap="md">
        {request && (
          <Text size="sm" c="dimmed">
            {request.competency.title}
          </Text>
        )}
        <MultiSelect
          label={t('reviewRequests.selectReviewers')}
          description={t('reviewRequests.selectReviewersHint')}
          placeholder={t('reviewRequests.selectReviewers')}
          data={users
            // Уволенных (архив) в рецензенты не назначить.
            .filter((u: User) => u._id !== applicantId && u.active !== false)
            .map((u: User) => ({ value: u._id, label: `${u.name} (${u.email})` }))}
          value={selected}
          onChange={setSelected}
          searchable
          clearable
          disabled={isLoading}
          maxDropdownHeight={260}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="light" color="gray" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => onSave(selected)} loading={isSaving}>
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
